/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf, params, shape, string, useDataModel, extend
} from '@thd-nucleus/data-sources';
import { Typography } from '@one-thd/sui-atomic-components';
import { ProductImage } from '@thd-olt-component-react/product-pod';
import { AddToContext } from '../../AddToContext';

const MAX_THUMBNAILS = 5;

export const AddToProductDetails = ({ products }) => {
  const {
    storeId,
    selectedProductDetails
  } = useContext(AddToContext);

  const itemid = products?.length > 0 ? products[0]?.itemId : selectedProductDetails?.itemId;

  const { data } = useDataModel('product', {
    variables: {
      itemId: itemid,
      storeId
    },
    skip: products?.length > 1 || !itemid
  });

  // Note: non-shared qc items do not have itemid
  if (!itemid) {
    return null;
  }

  const { product } = data || {};

  const brandName = product?.identifiers?.brandName || product?.identifiers?.brandName;
  const productLabel = product?.identifiers?.productLabel || product?.identifiers?.productLabel;

  const image = product?.media?.images?.find(
    (img) => img.type === 'IMAGE' && img.subType === 'PRIMARY'
  );

  return (
    <div className="sui-flex">
      {products?.length === 1 && image && (
        <img
          src={image?.url.replace('<SIZE>', '145')}
          alt={productLabel}
          className="sui-aspect-square sui-max-w-[20%] sm:sui-max-w-[30%] sui-object-contain"
          width={90}
          height={90}
        />
      )}
      {products?.length > 1 && (
        <div className="sui-flex sui-gap-2 sui-items-center">
          {products.slice(0, MAX_THUMBNAILS).map((prod, index) => {
            const isLastSlot = index === MAX_THUMBNAILS - 1 && products.length > MAX_THUMBNAILS;
            const isIntent = prod.itemType === 'intent' || !prod.itemId;
            const key = prod.itemId || `intent-${index}`;
            const plusSeparator = index === 1 ? (
              <div key={`plus-${index}`} className="sui-px-2px">
                <Typography variant="body-lg" weight="bold" height="tight">+</Typography>
              </div>
            ) : null;

            let thumbnail;
            if (isLastSlot) {
              thumbnail = (
                <div
                  key={key}
                  className="sui-flex-shrink-0 sui-bg-strong sui-aspect-square sui-grid sui-place-items-center"
                  style={{ width: 70, height: 70 }}
                >
                  <Typography variant="body-base" weight="bold">
                    +{products.length - MAX_THUMBNAILS + 1}
                  </Typography>
                </div>
              );
            } else if (isIntent) {
              thumbnail = (
                <div
                  key={key}
                  className="sui-flex-shrink-0 sui-bg-strong sui-aspect-square"
                  style={{ width: 70, height: 70 }}
                />
              );
            } else {
              thumbnail = (
                <div
                  key={key}
                  className="sui-flex-shrink-0 sui-overflow-hidden"
                  style={{ width: 70, height: 70 }}
                >
                  <ProductImage itemId={prod.itemId} />
                </div>
              );
            }

            return [plusSeparator, thumbnail];
          })}
        </div>
      )}
      <div className="sui-pl-6">
        {product && (
          <Typography variant="h6" lineClamp="2">
            {brandName}&nbsp;<Typography component="span">{productLabel}</Typography>
          </Typography>
        )}
        {products?.length === 1 && products[0]?.quantity > 1 && (
          <div className="sui-mt-2">
            <Typography weight="bold" variant="body-sm">
              Quantity: {products[0].quantity}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

AddToProductDetails.dataModel = extend(
  ProductImage,
  {
    product: params({
      itemId: string().isRequired()
    }).shape({
      identifiers: shape({
        brandName: string(),
        productLabel: string(),
        storeSkuNumber: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      })
    })
  }
);

AddToProductDetails.displayName = 'AddToProductDetails';

AddToProductDetails.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  }))
};

AddToProductDetails.defaultProps = {
  products: []
};
